import { Component, OnInit } from '@angular/core';
import { MatOptionSelectionChange } from '@angular/material/core';
import { TranslateService } from '@ngx-translate/core';
import { MEBrick } from 'src/app/common/activities/me-meta';
import { MeValidation } from '../../me-validation/me-validation';
import { MeBaseComponent } from '../me-base/me-base.component';
import { IMeBrickBuilder } from '../me-brick.builder';
import { TipsData } from '../../me-tips/me-tips.service';
import { SelectListItems } from 'src/app/common/services/external-settings/external-settings.interface';
import { ResponsiveDesignService } from 'src/app/common/services/responsive-design/responsive-design.service';

@Component({
    selector: 'app-me-select',
    templateUrl: './me-select.component.html',
    styleUrls: ['./me-select.component.less'],
    standalone: false
})
export class MeSelectComponent extends MeBaseComponent implements OnInit {

  override brick!: MeSelectBrick;
  tipsData!: TipsData;

  constructor(translate: TranslateService, protected override srvRpDesign: ResponsiveDesignService) {
    super(translate, srvRpDesign);
  }

  getText(item: any) {
    if (this.brick.setOptionText) {
      return this.brick.setOptionText(item);
    } else {
      return '!!INCONCEIVABLE!!';
    }
  }
  ngOnInit(): void {
    // console.log('me-select', this.brick);  
    this.tipsData = {
      label: this.brick.label,
      tipsMessage: this.brick.tipsMessage,
    };
  }

  onSelectionChange(event: MatOptionSelectionChange, item: any): void {
    if (event.isUserInput && this.brick.onSelectionChange) {
      this.brick.onSelectionChange(item);
    }
  }

  override updateComponentValue(value: any) {
    if ((this.brick.onSelectionChange) && (value != this.brick.value)) {
      this.brick.onSelectionChange(value);
    }
  };
}

export class MeSelectBrick extends MEBrick {

  constructor(
    name: string,
    index: number,
    label: string,
    value: any,
    public itemList?: any[],
    public onSelectionChange?: (item: any) => void,
    public setOptionText?: (item: any) => string,
    public setOptionValue?: (item: any) => any,
    validation: MeValidation[] = [],
    tipsMessage: string[] = [],
    span: boolean = false,
    getReviewDisplay = MEBrick.DEFAULT_REVIEW_DISPLAY,
    reviewIndex: number = index,
    summaryIndex = index,
    isDisabled: boolean = MEBrick.CONTROL_ENABLED,
    compressed: boolean = false,
    getSummaryDisplay = MEBrick.DEFAULT_SUMMARY_DISPLAY,
    compareFn?: (o1: any, o2: any)=>boolean) {
    super(name, index, MEBrick.SELECT, label, value, validation, tipsMessage, span,
      getReviewDisplay, reviewIndex, summaryIndex, undefined, undefined,
      undefined, undefined, undefined, undefined, getSummaryDisplay)
    this.isDisabled = isDisabled;
    this.compressed = compressed;
    this.compareFn = compareFn!;
  }

  public getValue() {
    if (this.setOptionValue) {
      return this.setOptionValue(this.value);
    }
    return this.value
  }

  public static DEFAULT_SET_OPTION_TEXT(value: any): string {
    return value;
  }

  public static DEFAULT_SET_OPTION_VALUE(value: any): string {
    return value;
  }

}


/**
 * Builder Pattern
 */
export class MeSelectBrickBuilder implements IMeBrickBuilder {

  protected name: string;
  protected index: number;
  protected label: string;
  protected value: any = null;
  protected itemList: any[];
  protected onSelectionChange?: (item: any) => void;
  protected setOptionText = MeSelectBrick.DEFAULT_SET_OPTION_TEXT;
  protected setOptionValue = MeSelectBrick.DEFAULT_SET_OPTION_VALUE;
  protected validation: MeValidation[] = MEBrick.NO_VALIDATION;
  protected compareFn: (o1: any, o2: any)=>boolean = MEBrick.DEFAULT_COMPARE_FN;
  protected tipsMessage: string[] = MEBrick.NO_TIPS_MESSAGE;
  protected span: boolean = MEBrick.NO_SPAN_ENTIRE_ROW;
  protected getReviewDisplay = MEBrick.DEFAULT_REVIEW_DISPLAY;
  protected reviewIndex: number = MEBrick.NOT_SHOW_IN_REVIEW; // Will be override back to index in the constructor
  protected summaryIndex: number = MEBrick.NOT_SHOW_IN_SUMMARY;
  protected isDisabled: boolean = MEBrick.CONTROL_ENABLED;
  protected compressed: boolean = false;

  constructor(name: string, index: number, label: string, itemList: any[]) {
    this.name = name;
    this.index = index;
    this.label = label;
    this.itemList = itemList;
    this.reviewIndex = index;
    this.summaryIndex = index;
  }

  public build(): MeSelectBrick {
    return new MeSelectBrick(
      this.name, this.index, this.label, this.value, this.itemList,
      this.onSelectionChange, this.setOptionText, this.setOptionValue,
      this.validation, this.tipsMessage, this.span, this.getReviewDisplay,
      this.reviewIndex, this.summaryIndex, this.isDisabled, this.compressed, undefined, this.compareFn);
  }

  public withValue(value: any): MeSelectBrickBuilder {
    this.value = value;
    return this;
  }

  public withOnSelectionChange(value: (item: any) => void): MeSelectBrickBuilder {
    this.onSelectionChange = value;
    return this;
  }

  public withSetOptionText(value: (item: any) => string): MeSelectBrickBuilder {
    this.setOptionText = value;
    return this;
  }

  public withSetOptionValue(value: (item: any) => any): MeSelectBrickBuilder {
    this.setOptionValue = value;
    return this;
  }

  public withValidation(value: MeValidation[]): MeSelectBrickBuilder {
    this.validation = value;
    return this;
  }

  public withCompareFn(value: (o1: any, o2: any)=>boolean): MeSelectBrickBuilder {
    this.compareFn = value;
    return this;
  }

  public withTipsMessage(value: string[]): MeSelectBrickBuilder {
    this.tipsMessage = value;
    return this;
  }

  public withSpan(value: boolean): MeSelectBrickBuilder {
    this.span = value;
    return this;
  }

  public withGetReviewDisplay(value: (value: any) => any): MeSelectBrickBuilder {
    this.getReviewDisplay = value;
    return this;
  }

  public withReviewIndex(value: number): MeSelectBrickBuilder {
    this.reviewIndex = value;
    return this;
  }

  public withSummaryIndex(value: number): MeSelectBrickBuilder {
    this.summaryIndex = value;
    return this;
  }

  public withIsDisabled(value: boolean): MeSelectBrickBuilder {
    this.isDisabled = value;
    return this;
  }

  public withCompressed(value: boolean): MeSelectBrickBuilder {
    this.compressed = value;
    return this;
  }
}

export function buildSimpleSelectedItemsList(name: string, index: number, label: string, itemList: SelectListItems[], validators: MeValidation[] = []): MeSelectBrickBuilder {
  let builder = new MeSelectBrickBuilder(name, index, label, itemList);
  builder.withValidation(validators);
  builder.withValue(itemList.find(item => item.isDefault));
  builder.withGetReviewDisplay((item: any) => item.value);
  builder.withSetOptionText((opt: SelectListItems) => opt.value);
  return builder;
}

export function buildSimpleItemsList(name: string, index: number, label: string, itemList: SimpleListItem[], validators: MeValidation[] = []): MeSelectBrickBuilder {
  let builder = new MeSelectBrickBuilder(name, index, label, itemList);
  builder.withValidation(validators);
  builder.withGetReviewDisplay((item: any) => item.value);
  builder.withSetOptionText((opt: SimpleListItem) => opt.value);
  return builder;
}

export interface SimpleListItem {
  code: string;
  value: string;
}